import { useState, useEffect, useRef } from "react";
import AtmosphereCanvas from "./atmosphere-canvas";
import Surface4 from "./surfaces/Surface4";
import Surface5 from "./surfaces/Surface5";
import Surface6 from "./surfaces/Surface6";
import Surface7 from "./surfaces/Surface7";
import Surface8 from "./surfaces/Surface8";
import Surface9 from "./surfaces/Surface9";
import Surface10 from "./surfaces/Surface10";
import Surface11 from "./surfaces/Surface11";
import Surface12 from "./surfaces/Surface12";
import Surface13 from "./surfaces/Surface13";
import Surface14 from "./surfaces/Surface14";
import Surface15 from "./surfaces/Surface15";
import Surface16 from "./surfaces/Surface16";
import Surface17 from "./surfaces/Surface17";
import useDecodedImage from "./hooks/useDecodedImage";

const LIMITS = { text: 100, textarea: 1000 };
const stripHtml = (str) => str.replace(/<[^>]*>/g, "");

const COMFORT_PARTICLES = [
  { left: "8%",  top: "55%", size: 1.5, opacity: 0.2,  dur: "16s", delay: "0s",   travel: 420 },
  { left: "25%", top: "70%", size: 1,   opacity: 0.18, dur: "19s", delay: "2.5s", travel: 580 },
  { left: "55%", top: "65%", size: 1.5, opacity: 0.22, dur: "14s", delay: "1.8s", travel: 350 },
  { left: "72%", top: "80%", size: 1,   opacity: 0.15, dur: "17s", delay: "4.0s", travel: 500 },
  { left: "40%", top: "85%", size: 2,   opacity: 0.2,  dur: "12s", delay: "3.2s", travel: 680 },
  { left: "88%", top: "60%", size: 1,   opacity: 0.18, dur: "15s", delay: "1.1s", travel: 420 },
];

const COMFORT_PARTICLES_DESKTOP = [
  { left: "5%",  top: "60%", size: 2,   opacity: 0.18, dur: "18s", delay: "0.5s", travel: 460 },
  { left: "18%", top: "78%", size: 1.5, opacity: 0.2,  dur: "14s", delay: "3.0s", travel: 620 },
  { left: "62%", top: "72%", size: 2,   opacity: 0.15, dur: "17s", delay: "1.4s", travel: 380 },
  { left: "78%", top: "55%", size: 1.5, opacity: 0.2,  dur: "13s", delay: "4.8s", travel: 540 },
  { left: "90%", top: "82%", size: 2,   opacity: 0.18, dur: "16s", delay: "2.2s", travel: 700 },
  { left: "48%", top: "90%", size: 1.5, opacity: 0.15, dur: "19s", delay: "0.9s", travel: 320 },
];

const QUESTIONS = [
  {
    id: "introducerName",
    label: "What\u2019s your name?",
    type: "text",
    placeholder: "Your first name",
  },
  {
    id: "recipientName",
    label: "And who is this gift for?",
    type: "text",
    placeholder: "Their first name...",
  },
  {
    id: "relationship",
    label: "How do you know them?",
    type: "choice",
    options: [
      "Partner / spouse",
      "Family",
      "Family — extended",
      "Best friend",
      "Close friend",
      "Friend",
      "Coworker / colleague",
      "Mentor / mentee",
      "Other",
    ],
  },
  {
    id: "tech",
    label: "How comfortable are they with technology?",
    type: "choice",
    options: [
      "Avoids it if possible",
      "Uses it but doesn't love it",
      "Comfortable enough",
      "They'd probably build this themselves",
    ],
  },
  {
    id: "work",
    label: "What do they do for work?",
    type: "text",
    placeholder: "Their work or career...",
  },
  {
    id: "outside",
    label: "What do they enjoy outside of work?",
    type: "textarea",
    placeholder: "Hobbies, interests, things they actually choose to do...",
  },
  {
    id: "personality",
    label: "How would you describe their personality?",
    type: "textarea",
    placeholder: "Like you're telling a friend about them. Honest, not polished...",
  },
  {
    id: "speech_style",
    label: "How do they talk?",
    type: "multi-choice",
    options: [
      "Reserved",
      "Direct",
      "Warm",
      "Dry",
      "Playful",
      "Thoughtful",
      "Nerdy",
      "Salty",
    ],
  },
  {
    id: "season",
    label: "What's the season they're in right now?",
    type: "textarea",
    placeholder: "Anything big, tender, stressful, exciting, or changing lately...",
    optional: true,
  },
  {
    id: "downplay",
    label: "What are they good at that they probably downplay?",
    type: "textarea",
    placeholder: "The thing you see in them that they shrug off...",
  },
  {
    id: "lightup",
    label: "Tell me about a time you saw them light up.",
    type: "textarea",
    placeholder: "What were they talking about or doing? Be as specific as you can...",
  },
  {
    id: "avoid",
    label: "Anything the AI should know or avoid?",
    type: "textarea",
    placeholder: "Sensitive topics, sore spots, things to steer clear of. Optional but really helpful...",
    optional: true,
  },
];

const INTRO_MESSAGE = "Thank you for doing this. To make this conversation feel truly personal, we need to know a little about them. It'll take about 5 minutes — just answer like you're describing them to a good friend. The more specific you are, the more the conversation will feel like it actually knows them. Your answers are used only to shape this conversation — they'll never see what you wrote, and neither will anyone else.";
const V2_BACKGROUND_URLS = {
  intro: "/backgrounds/tgc-124/threshold-continuation-v1.png",
  review: "/backgrounds/tgc-124/threshold-continuation-v1.png",
  shortText: "/backgrounds/tgc-124/threshold-continuation-v1.png",
  textarea: "/backgrounds/tgc-124/threshold-continuation-v1.png",
  singleSelect: "/backgrounds/tgc-124/threshold-continuation-v1.png",
  multiSelect: "/backgrounds/tgc-124/threshold-continuation-v1.png",
};

function sanitizeDraft(draft) {
  if (!draft || typeof draft !== "object") return null;
  const phase = ["intro", "questions", "review"].includes(draft.phase)
    ? draft.phase
    : "intro";
  const currentQ = Number.isInteger(draft.currentQ)
    ? Math.min(Math.max(draft.currentQ, 0), QUESTIONS.length - 1)
    : 0;
  return {
    phase,
    currentQ,
    answers: draft.answers && typeof draft.answers === "object" ? draft.answers : {},
    inputVal: typeof draft.inputVal === "string" ? draft.inputVal : "",
  };
}

export default function ComfortProfileForm({ onComplete, draftStorageKey, initialDraft }) {
  const restoredDraft = sanitizeDraft(initialDraft);
  const restoredDraftRef = useRef(restoredDraft);
  const [phase, setPhase] = useState(restoredDraft?.phase || "intro");
  const [currentQ, setCurrentQ] = useState(restoredDraft?.currentQ || 0);
  const [answers, setAnswers] = useState(restoredDraft?.answers || {});
  const [inputVal, setInputVal] = useState(restoredDraft?.inputVal || "");
  const [showButton, setShowButton] = useState(false);
  const [introVisible, setIntroVisible] = useState(false);
  const [questionVisible, setQuestionVisible] = useState(false);
  const [inputError, setInputError] = useState("");
  const [transitionDirection, setTransitionDirection] = useState("forward");
  const [editQuestionId, setEditQuestionId] = useState(null);
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Lock body scroll — prevents iOS from scrolling the page when keyboard opens
    const savedOverflow = document.body.style.overflow;
    const savedPosition = document.body.style.position;
    const savedWidth = document.body.style.width;
    const savedTop = document.body.style.top;
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollY}px`;

    let rafId = null;
    const update = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const vv = window.visualViewport;
        const height = vv ? vv.height : window.innerHeight;
        const offsetTop = vv ? Math.max(0, vv.offsetTop) : 0;
        document.documentElement.style.setProperty("--app-height", `${height}px`);
        document.documentElement.style.setProperty("--app-top", `${offsetTop}px`);
        rafId = null;
      });
    };
    update();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.body.style.overflow = savedOverflow;
      document.body.style.position = savedPosition;
      document.body.style.width = savedWidth;
      document.body.style.top = savedTop;
      window.scrollTo(0, scrollY);
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      }
    };
  }, []);

  useEffect(() => {
    if (phase === "intro") {
      setTimeout(() => setIntroVisible(true), 300);
      setTimeout(() => setShowButton(true), 2200);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === "questions") {
      setQuestionVisible(false);
      setTimeout(() => setQuestionVisible(true), 300);
      const initialRestore = restoredDraftRef.current;
      if (
        initialRestore &&
        initialRestore.phase === "questions" &&
        initialRestore.currentQ === currentQ
      ) {
        setInputVal(initialRestore.inputVal || answers[QUESTIONS[currentQ].id] || "");
      } else {
        setInputVal(answers[QUESTIONS[currentQ].id] || "");
      }
      restoredDraftRef.current = null;
      setInputError("");
      if (chatRef.current) chatRef.current.scrollTop = 0;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ, phase]);

  useEffect(() => {
    if (!draftStorageKey) return undefined;
    const timeout = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          draftStorageKey,
          JSON.stringify({
            version: 1,
            phase,
            currentQ,
            answers,
            inputVal,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        // Local recovery is best-effort; payment recovery remains available by Gift Code.
      }
    }, 120);
    return () => window.clearTimeout(timeout);
  }, [answers, currentQ, draftStorageKey, inputVal, phase]);

  const handleStart = () => {
    setTransitionDirection("forward");
    setPhase("questions");
  };

  const handleAnswer = (val) => {
    const raw = val !== undefined ? val : inputVal.trim();
    const value = typeof raw === "string" ? stripHtml(raw) : raw;
    if (!value && !QUESTIONS[currentQ].optional) return;
    setInputError("");
    const newAnswers = { ...answers, [QUESTIONS[currentQ].id]: value || "" };
    setAnswers(newAnswers);
    if (currentQ < QUESTIONS.length - 1) {
      setTransitionDirection("forward");
      setCurrentQ(currentQ + 1);
    } else {
      setPhase("review");
    }
  };

  const handleBack = () => {
    if (currentQ > 0) {
      const trimmed = typeof inputVal === "string" ? inputVal.trim() : "";
      if (trimmed && !["choice", "multi-choice"].includes(QUESTIONS[currentQ]?.type)) {
        const value = stripHtml(trimmed);
        setAnswers((existing) => ({
          ...existing,
          [QUESTIONS[currentQ].id]: value,
          ...(currentQ === 1 ? { recipient_first_name: value } : {}),
          ...(QUESTIONS[currentQ]?.id === "lightup" ? { recipient_light_up: value } : {}),
        }));
        setInputVal(value);
      }
      setTransitionDirection("back");
      setCurrentQ(currentQ - 1);
    } else {
      const trimmed = inputVal.trim();
      if (trimmed) {
        setAnswers((existing) => ({ ...existing, [QUESTIONS[currentQ].id]: stripHtml(trimmed) }));
      }
      setTransitionDirection("back");
      setPhase("intro");
      setShowButton(true);
      setIntroVisible(true);
    }
  };

  const handleEdit = (id) => {
    const idx = QUESTIONS.findIndex((q) => q.id === id);
    setCurrentQ(idx);
    setInputVal(answers[id] || "");
    setEditQuestionId(id);
    setTransitionDirection("forward");
    setPhase("questions");
  };

  const handleSubmit = () => {
    const speechStyle = Array.isArray(answers.speech_style)
      ? answers.speech_style.slice(0, 4)
      : [];
    if (onComplete) onComplete({ ...answers, speech_style: speechStyle });
  };

  const finishEdit = () => {
    setEditQuestionId(null);
    setTransitionDirection("back");
    setPhase("review");
  };

  const saveTextAnswer = (id, value) => {
    const aliases = {
      recipientName: { recipient_first_name: value },
      tech: { tech_comfort: value },
      work: { recipient_work: value },
      outside: { recipient_enjoys: value },
      personality: { recipient_personality: value },
      season: { recipient_season: value },
      downplay: { recipient_downplays: value },
      lightup: { recipient_light_up: value },
      avoid: { recipient_avoid: value },
    };
    setAnswers((existing) => ({
      ...existing,
      [id]: value,
      ...(aliases[id] || {}),
    }));
  };

  const handleTextNext = (id, required = true) => {
    const trimmed = inputVal.trim();
    if (required && !trimmed) {
      setInputVal(trimmed);
      setInputError("required");
      return;
    }
    const value = stripHtml(trimmed);
    setInputVal(value);
    setInputError("");
    saveTextAnswer(id, value);
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setPhase("review");
    }
  };

  const handleTextSkip = (id) => {
    setInputVal("");
    setInputError("");
    saveTextAnswer(id, "");
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setPhase("review");
    }
  };

  const handleTextChange = (value, limit = 2000) => {
    setInputVal(value.slice(0, limit));
    if (value.trim()) setInputError("");
  };

  const handleTextBlur = () => {
    setInputVal((value) => value.trim());
  };

  const handleSurface5Next = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) {
      setInputVal(trimmed);
      setInputError("required");
      return;
    }
    const value = stripHtml(trimmed);
    setInputVal(value);
    setInputError("");
    setAnswers((existing) => ({ ...existing, introducerName: value }));
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    setCurrentQ(1);
  };

  const handleSurface5Change = (value) => {
    setInputVal(value.slice(0, 80));
    if (value.trim()) setInputError("");
  };

  const handleSurface5Blur = () => {
    setInputVal((value) => value.trim());
  };

  const handleSurface6Next = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) {
      setInputVal(trimmed);
      setInputError("required");
      return;
    }
    const value = stripHtml(trimmed);
    setInputVal(value);
    setInputError("");
    setAnswers((existing) => ({
      ...existing,
      recipientName: value,
      recipient_first_name: value,
    }));
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    setCurrentQ(2);
  };

  const handleSurface6Change = (value) => {
    setInputVal(value.slice(0, 80));
    if (value.trim()) setInputError("");
  };

  const handleSurface6Blur = () => {
    setInputVal((value) => value.trim());
  };

  const handleSurface7Change = (value) => {
    setAnswers((existing) => ({ ...existing, relationship: value }));
    setInputError("");
  };

  const handleSurface7Next = (value) => {
    const relationship = value || answers.relationship;
    if (!relationship) {
      setInputError("required");
      return;
    }
    setAnswers((existing) => ({ ...existing, relationship }));
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    setCurrentQ(3);
  };

  const handleSurface8Change = (value) => {
    setAnswers((existing) => ({
      ...existing,
      tech: value,
      tech_comfort: value,
    }));
    setInputError("");
  };

  const handleSurface8Next = (value) => {
    const techComfort = value || answers.tech;
    if (!techComfort) {
      setInputError("required");
      return;
    }
    setAnswers((existing) => ({
      ...existing,
      tech: techComfort,
      tech_comfort: techComfort,
    }));
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    setCurrentQ(4);
  };

  const handleSurface12Change = (value) => {
    const selected = Array.isArray(value) ? value.slice(0, 4) : [];
    setAnswers((existing) => ({ ...existing, speech_style: selected }));
    setInputError("");
  };

  const handleSurface12Next = () => {
    const selected = Array.isArray(answers.speech_style) ? answers.speech_style.slice(0, 4) : [];
    if (selected.length === 0) {
      setInputError("required");
      return;
    }
    setAnswers((existing) => ({ ...existing, speech_style: selected }));
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    setCurrentQ(currentQ + 1);
  };

  const handleSurface15Next = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) {
      setInputVal(trimmed);
      setInputError("required");
      return;
    }
    const value = stripHtml(trimmed);
    setInputVal(value);
    setInputError("");
    setAnswers((existing) => ({
      ...existing,
      lightup: value,
      recipient_light_up: value,
    }));
    if (editQuestionId) {
      finishEdit();
      return;
    }
    setTransitionDirection("forward");
    setCurrentQ(currentQ + 1);
  };

  const handleSurface15Change = (value) => {
    setInputVal(value.slice(0, 2000));
    if (value.trim()) setInputError("");
  };

  const handleSurface15Blur = () => {
    setInputVal((value) => value.trim());
  };

  const q = QUESTIONS[currentQ];
  const isSurface5 = phase === "questions" && currentQ === 0;
  const isSurface4 = phase === "intro";
  const isSurface6 = phase === "questions" && currentQ === 1;
  const isSurface7 = phase === "questions" && currentQ === 2;
  const isSurface8 = phase === "questions" && currentQ === 3;
  const isSurface9 = phase === "questions" && q?.id === "work";
  const isSurface10 = phase === "questions" && q?.id === "outside";
  const isSurface11 = phase === "questions" && q?.id === "personality";
  const isSurface12 = phase === "questions" && q?.id === "speech_style";
  const isSurface13 = phase === "questions" && q?.id === "season";
  const isSurface14 = phase === "questions" && q?.id === "downplay";
  const isSurface15 = phase === "questions" && q?.id === "lightup";
  const isSurface16 = phase === "questions" && q?.id === "avoid";
  const isSurface17 = phase === "review";
  const isV2QuestionSurface = isSurface5 || isSurface6 || isSurface7 || isSurface8 || isSurface9 || isSurface10 || isSurface11 || isSurface12 || isSurface13 || isSurface14 || isSurface15 || isSurface16;
  const isV2Surface = isSurface4 || isV2QuestionSurface || isSurface17;
  const v2BackgroundUrl = (() => {
    if (isSurface4) return V2_BACKGROUND_URLS.intro;
    if (isSurface17) return V2_BACKGROUND_URLS.review;
    if (!isV2QuestionSurface) return null;
    if (q?.type === "multi-choice") return V2_BACKGROUND_URLS.multiSelect;
    if (q?.type === "choice") return V2_BACKGROUND_URLS.singleSelect;
    if (q?.type === "textarea") return V2_BACKGROUND_URLS.textarea;
    return V2_BACKGROUND_URLS.shortText;
  })();
  const v2BackgroundReady = useDecodedImage(v2BackgroundUrl);
  const recipientName = answers.recipientName || "them";
  const introducerName = answers.introducerName || "";
  const editMode = Boolean(editQuestionId);

  return (
    <div className="comfort-bg" data-v2-surface={isV2Surface ? "true" : "false"} data-v2-bg-ready={v2BackgroundReady ? "true" : "false"} style={{
      position: "fixed",
      top: "var(--app-top, 0px)", left: 0, right: 0,
      height: "var(--app-height, 100dvh)",
      display: "flex",
      flexDirection: "column",
      color: "var(--tgc-world-ink)",
      fontFamily: "var(--tgc-world-font-body)",
      overflow: "hidden",
      background: "var(--tgc-world-cream)",
    }}>
      {!isV2Surface && <div className="comfort-bg-layer" aria-hidden="true" />}
      {v2BackgroundUrl && (
        <div
          className={`tgc-v2-static-bg${v2BackgroundReady ? " tgc-v2-static-bg--ready" : ""}`}
          aria-hidden="true"
          style={{ "--tgc-v2-bg-image": `url("${v2BackgroundUrl}")` }}
        />
      )}
      {!isV2Surface && (phase === "intro" || phase === "questions" || phase === "review") && <AtmosphereCanvas />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        textarea:focus, input:focus { outline: none; }
        textarea { resize: none; }
        html, body { overscroll-behavior: none; background: #1a1714; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #3a332a; border-radius: 2px; }
        @keyframes floatDust {
          0%   { transform: translateY(0px); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 0.7; }
          100% { transform: translateY(var(--travel,-130px)); opacity: 0; }
        }
        @keyframes bgFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes titleGlow {
          0%, 100% { filter: drop-shadow(0 0 14px rgba(232,183,107,0.55)) drop-shadow(2px 4px 10px rgba(0,0,0,0.55)); }
          50% { filter: drop-shadow(0 0 30px rgba(232,183,107,0.9)) drop-shadow(2px 4px 10px rgba(0,0,0,0.55)); }
        }
        .comfort-bg {
          animation: bgFadeIn 0.8s ease forwards;
        }
        .comfort-bg[data-v2-surface="true"][data-v2-bg-ready="false"] .tgc-v2-surface {
          opacity: 0;
        }
        .comfort-bg[data-v2-surface="true"][data-v2-bg-ready="true"] .tgc-v2-surface {
          opacity: 1;
          transition: opacity 180ms ease-out;
        }
        .comfort-bg-layer {
          position: fixed;
          inset: 0;
          z-index: -1;
          background-image: linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url('/backgrounds/comfort-profile-mobile.jpg');
          background-size: cover;
          background-position: center;
          pointer-events: none;
        }
        @media (min-width: 768px) {
          .comfort-bg-layer {
            background-image: linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url('/backgrounds/comfort-profile-desktop.jpg') !important;
          }
        }
        .comfort-title {
          animation: titleGlow 3s ease-in-out 0.8s infinite;
        }
        .comfort-dust-desktop { display: none; }
        .comfort-intro {
          justify-content: flex-start !important;
          padding-top: 8vh !important;
        }
        @media (min-width: 768px) {
          .comfort-dust-desktop { display: block; }
          .comfort-dust-particle { transform: scale(1.8); }
          .comfort-intro {
            justify-content: center !important;
            padding-top: 0 !important;
          }
          .comfort-form-inner {
            max-width: 560px;
            margin-left: auto;
            margin-right: auto;
          }
          .comfort-question-bubble {
            border-bottom-left-radius: 16px !important;
          }
          .comfort-question-wrap {
            padding-top: 4vh !important;
          }
          .comfort-question-text {
            font-size: 28px !important;
          }
        }
      `}</style>

      {isSurface4 && (
        <Surface4
          direction={transitionDirection}
          onBegin={handleStart}
        />
      )}

      {isSurface5 && (
        <Surface5
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={handleSurface5Change}
          onBlur={handleSurface5Blur}
          onBack={handleBack}
          onNext={handleSurface5Next}
          editMode={editMode}
        />
      )}

      {isSurface6 && (
        <Surface6
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={handleSurface6Change}
          onBlur={handleSurface6Blur}
          onBack={handleBack}
          onNext={handleSurface6Next}
          editMode={editMode}
        />
      )}

      {isSurface7 && (
        <Surface7
          value={answers.relationship || ""}
          direction={transitionDirection}
          onChange={handleSurface7Change}
          onBack={handleBack}
          onNext={handleSurface7Next}
          editMode={editMode}
        />
      )}

      {isSurface8 && (
        <Surface8
          value={answers.tech || ""}
          direction={transitionDirection}
          onChange={handleSurface8Change}
          onBack={handleBack}
          onNext={handleSurface8Next}
          editMode={editMode}
        />
      )}

      {isSurface9 && (
        <Surface9
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={(value) => handleTextChange(value, 100)}
          onBlur={handleTextBlur}
          onBack={handleBack}
          onNext={() => handleTextNext("work")}
          editMode={editMode}
        />
      )}

      {isSurface10 && (
        <Surface10
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onBack={handleBack}
          onNext={() => handleTextNext("outside")}
          editMode={editMode}
        />
      )}

      {isSurface11 && (
        <Surface11
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onBack={handleBack}
          onNext={() => handleTextNext("personality")}
          editMode={editMode}
        />
      )}

      {isSurface12 && (
        <Surface12
          value={answers.speech_style || []}
          direction={transitionDirection}
          onChange={handleSurface12Change}
          onBack={handleBack}
          onNext={handleSurface12Next}
          editMode={editMode}
        />
      )}

      {isSurface13 && (
        <Surface13
          value={inputVal}
          direction={transitionDirection}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onBack={handleBack}
          onNext={() => handleTextNext("season", false)}
          onSkip={() => handleTextSkip("season")}
          editMode={editMode}
        />
      )}

      {isSurface14 && (
        <Surface14
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onBack={handleBack}
          onNext={() => handleTextNext("downplay")}
          editMode={editMode}
        />
      )}

      {isSurface15 && (
        <Surface15
          value={inputVal}
          error={inputError}
          direction={transitionDirection}
          onChange={handleSurface15Change}
          onBlur={handleSurface15Blur}
          onBack={handleBack}
          onNext={handleSurface15Next}
          editMode={editMode}
        />
      )}

      {isSurface16 && (
        <Surface16
          value={inputVal}
          direction={transitionDirection}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onBack={handleBack}
          onNext={() => handleTextNext("avoid", false)}
          onSkip={() => handleTextSkip("avoid")}
          editMode={editMode}
        />
      )}

      {isSurface17 && (
        <Surface17
          answers={answers}
          introducerName={introducerName}
          recipientName={recipientName}
          onEdit={handleEdit}
          onSend={handleSubmit}
        />
      )}

      {/* Dust particles */}
      {!isV2Surface && <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        {COMFORT_PARTICLES_DESKTOP.map((p, i) => (
          <div key={`d${i}`} className="comfort-dust-desktop" style={{
            position: "absolute",
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            background: `rgba(232,183,107,${p.opacity})`,
            borderRadius: "50%",
            animation: `floatDust ${p.dur} ease-in-out ${p.delay} infinite`,
            '--travel': `-${p.travel}px`,
          }} />
        ))}
        {COMFORT_PARTICLES.map((p, i) => (
          <div key={`m${i}`} className="comfort-dust-particle" style={{
            position: "absolute",
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            background: `rgba(232,183,107,${p.opacity})`,
            borderRadius: "50%",
            animation: `floatDust ${p.dur} ease-in-out ${p.delay} infinite`,
            '--travel': `-${p.travel}px`,
          }} />
        ))}
      </div>}

      {/* Header */}
      {!isV2Surface && <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid rgba(232,183,107,0.25)",
        background: "rgba(232,183,107,0.07)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <div className="comfort-title" style={{
            fontFamily: "'Dancing Script', cursive",
            fontSize: 22,
            fontWeight: 600,
            background: "linear-gradient(180deg, #F5D08A, #C9A02F)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: 0.3,
          }}>
            The Gift Conversation
          </div>
          <div style={{
            fontSize: 11,
            color: "rgba(232,183,107,0.6)",
            marginTop: 1,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}>
            {phase === "review" ? "Review your answers" : "Tell us about them"}
          </div>
        </div>
        {phase === "questions" && (
          <div style={{
            fontSize: 12,
            color: "rgba(232,183,107,0.8)",
            background: "rgba(232,183,107,0.08)",
            border: "1px solid rgba(232,183,107,0.3)",
            padding: "5px 12px",
            borderRadius: 20,
          }}>
            {currentQ + 1} of {QUESTIONS.length}
          </div>
        )}
      </div>}

      {/* Question bubble */}
      {!isV2Surface && phase === "questions" && questionVisible && (
        <div className="comfort-form-inner comfort-question-wrap" style={{
          padding: "14px 16px 0",
          flexShrink: 0,
          animation: "fadeUp 0.4s ease forwards",
        }}>
          <div className="comfort-question-bubble" style={{
            background: "rgba(42, 36, 32, 0.82)",
            border: "1px solid rgba(232,183,107,0.3)",
            borderRadius: 16,
            borderBottomLeftRadius: 4,
            padding: "14px 18px",
          }}>
            <span className="comfort-question-text" style={{
              fontFamily: "'Dancing Script', cursive",
              fontSize: 24,
              background: "linear-gradient(180deg, #F5D08A, #C9A02F)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.4,
              display: "inline-block",
            }}>
              {q.label}
            </span>
            {q.optional && (
              <span style={{ fontSize: 12, color: "#9a8e7e", marginLeft: 8 }}>
                (optional)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Scrollable content area */}
      {!isV2Surface && <div ref={chatRef} style={{
        flex: 1,
        overflowY: "auto",
        padding: "12px 16px 8px",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}>

        {/* INTRO */}
        {phase === "intro" && (
          <div className="comfort-intro" style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "flex-start",
            alignItems: "center",
            paddingTop: "8vh",
          }}>
            {introVisible && (
              <div style={{
                animation: "fadeUp 0.6s ease forwards",
                marginBottom: 28,
                width: "min(85vw, 520px)",
                marginLeft: "auto",
                marginRight: "auto",
              }}>
                <div style={{
                  background: "rgba(232,183,107,0.08)",
                  border: "1px solid rgba(232,183,107,0.3)",
                  borderRadius: 16,
                  padding: "16px 20px",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "#e8e0d4",
                  textAlign: "center",
                }}>
                  {INTRO_MESSAGE}
                </div>
              </div>
            )}
            {showButton && (
              <div style={{ animation: "fadeUp 0.5s ease forwards", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, marginBottom: "8vh" }}>
                <button
                  onClick={handleStart}
                  style={{
                    background: "linear-gradient(180deg, #F5D08A, #C9A02F)",
                    color: "#1a1714",
                    border: "none",
                    padding: "13px 40px",
                    borderRadius: 50,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                    letterSpacing: 0.3,
                    transition: "all 0.2s",
                  }}
                >
                  Begin →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Choice buttons */}
        {phase === "questions" && questionVisible && q.type === "choice" && (
          <div className="comfort-form-inner" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {q.options.map((opt) => (
              <button
                key={opt}
                onClick={() => handleAnswer(opt)}
                style={{
                  background: answers[q.id] === opt ? "linear-gradient(180deg, #F5D08A, #C9A02F)" : "rgba(232,183,107,0.08)",
                  border: `1px solid ${answers[q.id] === opt ? "transparent" : "rgba(232,183,107,0.3)"}`,
                  color: answers[q.id] === opt ? "#1a1714" : "rgba(232,183,107,0.85)",
                  padding: "12px 18px",
                  borderRadius: 50,
                  fontSize: 14,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        )}

        {/* REVIEW */}
        {phase === "review" && null}

      </div>}

      {/* Input bar */}
      {!isV2Surface && phase === "questions" && !["choice", "multi-choice"].includes(q.type) && questionVisible && (
        <div style={{
          padding: "10px 14px 20px",
          borderTop: "1px solid rgba(232,183,107,0.25)",
          background: "rgba(232,183,107,0.07)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          flexShrink: 0,
          animation: "fadeUp 0.4s ease forwards",
        }}>
        <div className="comfort-form-inner">
          {q.type === "textarea" ? (
            <textarea
              ref={inputRef}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value.slice(0, LIMITS.textarea))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAnswer();
                }
              }}
              placeholder={q.placeholder}
              maxLength={LIMITS.textarea}
              rows={2}
              style={{
                width: "100%",
                background: "rgba(26, 23, 20, 0.7)",
                border: "1px solid rgba(232,183,107,0.25)",
                borderRadius: 12,
                color: "#e8e0d4",
                fontSize: 15,
                padding: "10px 14px",
                lineHeight: 1.5,
                fontFamily: "inherit",
                boxSizing: "border-box",
                marginBottom: 4,
              }}
            />
          ) : (
            <input
              ref={inputRef}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value.slice(0, LIMITS.text))}
              onKeyDown={(e) => { if (e.key === "Enter") handleAnswer(); }}
              placeholder={q.placeholder}
              maxLength={LIMITS.text}
              style={{
                width: "100%",
                background: "rgba(26, 23, 20, 0.7)",
                border: "1px solid rgba(232,183,107,0.25)",
                borderRadius: 12,
                color: "#e8e0d4",
                fontSize: 15,
                padding: "10px 14px",
                fontFamily: "inherit",
                boxSizing: "border-box",
                marginBottom: 4,
              }}
            />
          )}

          {(() => {
            const limit = q.type === "textarea" ? LIMITS.textarea : LIMITS.text;
            const remaining = limit - inputVal.length;
            if (remaining >= limit || remaining > 100) return null;
            return (
              <div style={{
                fontSize: 11,
                color: remaining <= 20 ? "#e87a6a" : "#9a8e7e",
                textAlign: "right",
                marginBottom: 6,
              }}>
                {remaining} character{remaining === 1 ? "" : "s"} remaining
              </div>
            );
          })()}

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={handleBack}
              style={{
                background: "rgba(232,183,107,0.08)",
                border: "1px solid rgba(232,183,107,0.3)",
                color: "rgba(232,183,107,0.8)",
                padding: "10px 18px",
                borderRadius: 50,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
            <button
              onClick={() => handleAnswer()}
              disabled={!inputVal.trim() && !q.optional}
              style={{
                flex: 1,
                background: (inputVal.trim() || q.optional) ? "linear-gradient(180deg, #F5D08A, #C9A02F)" : "rgba(232,183,107,0.08)",
                color: (inputVal.trim() || q.optional) ? "#1a1714" : "rgba(232,183,107,0.4)",
                border: (inputVal.trim() || q.optional) ? "none" : "1px solid rgba(232,183,107,0.2)",
                padding: "10px",
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 600,
                cursor: (inputVal.trim() || q.optional) ? "pointer" : "not-allowed",
              }}
            >
              {q.optional && !inputVal.trim() ? "Skip →" : "Next →"}
            </button>
          </div>
        </div>{/* /comfort-form-inner */}
        </div>
      )}

      {/* Back button for choice questions */}
      {!isV2Surface && phase === "questions" && q.type === "choice" && questionVisible && (
        <div style={{
          padding: "10px 14px 20px",
          borderTop: "1px solid rgba(232,183,107,0.25)",
          background: "rgba(232,183,107,0.07)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          flexShrink: 0,
        }}>
          <div className="comfort-form-inner">
            <button
              onClick={handleBack}
              style={{
                background: "rgba(232,183,107,0.08)",
                border: "1px solid rgba(232,183,107,0.3)",
                color: "rgba(232,183,107,0.8)",
                padding: "10px 18px",
                borderRadius: 50,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
